import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Avatar,
  Chip,
  Select,
  Option,
  IconButton,
  Tooltip,
} from '@mui/joy';
import { useQuery, useMutation } from '@apollo/client';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import EmailIcon from '@mui/icons-material/Email';

import useUser from './auth/useUser';
import { useFeedback } from './feedback/Service';
import { getCdnFileUrl, parseGraphQLError, formatTimestamp } from '../utils';
import { GET_TEAM_MEMBERS } from '../gql/team.queries';
import {
  REMOVE_TEAM_MEMBER,
  UPDATE_TEAM_MEMBER_ROLE,
  RESEND_INVITATION,
  TRANSFER_OWNERSHIP,
  LEAVE_CLIENT,
} from '../gql/team.mutations';
import useTeamPermissions from '../hooks/useTeamPermissions';
import { TEAM_ROLE_LABELS, TEAM_ROLE_COLORS } from '../constants';
import TeamInviteModal from './TeamInviteModal';
import LargeLoader from './LargeLoader';

const Team = () => {
  const { user } = useUser();
  const feedback = useFeedback();
  const client = user?.client;
  const permissions = useTeamPermissions(client);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_TEAM_MEMBERS, {
    variables: { clientId: client?._id },
    skip: !client?._id,
  });

  const [removeTeamMember, { loading: removing }] = useMutation(REMOVE_TEAM_MEMBER);
  const [updateTeamMemberRole, { loading: updating }] = useMutation(UPDATE_TEAM_MEMBER_ROLE);
  const [resendInvitation, { loading: resending }] = useMutation(RESEND_INVITATION);
  const [transferOwnership, { loading: transferring }] = useMutation(TRANSFER_OWNERSHIP);
  const [leaveClient, { loading: leaving }] = useMutation(LEAVE_CLIENT);

  const teamData = data?.getTeamMembers;
  const members = teamData?.members || [];
  const pendingInvites = teamData?.pendingInvites || [];

  const handleRemoveMember = async (member) => {
    const confirmed = await feedback.confirm({
      title: 'Remove Team Member',
      text: `Are you sure you want to remove ${member.user.firstName || member.user.email} from the team?`,
      confirmButtonText: 'Remove',
    });
    if (!confirmed) return;

    try {
      await removeTeamMember({
        variables: { clientId: client._id, userId: member.user._id },
      });
      feedback.snackbar({ text: 'Team member removed successfully', type: 'success' });
      refetch();
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (member.role === newRole) return;

    try {
      await updateTeamMemberRole({
        variables: { clientId: client._id, userId: member.user._id, newRole },
      });
      feedback.snackbar({ text: 'Role updated successfully', type: 'success' });
      refetch();
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const handleResendInvitation = async (invite) => {
    try {
      await resendInvitation({
        variables: { clientId: client._id, userId: invite.user._id },
      });
      feedback.snackbar({ text: 'Invitation resent successfully', type: 'success' });
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const handleCancelInvitation = async (invite) => {
    const confirmed = await feedback.confirm({
      title: 'Cancel Invitation',
      text: `Are you sure you want to cancel the invitation for ${invite.user.email}?`,
      confirmButtonText: 'Cancel Invitation',
    });
    if (!confirmed) return;

    try {
      await removeTeamMember({
        variables: { clientId: client._id, userId: invite.user._id },
      });
      feedback.snackbar({ text: 'Invitation cancelled', type: 'success' });
      refetch();
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const handleTransferOwnership = async (member) => {
    const confirmed = await feedback.confirm({
      title: 'Transfer Ownership',
      text: `Are you sure you want to transfer ownership to ${member.user.firstName || member.user.email}? You will become an admin.`,
      confirmButtonText: 'Transfer',
    });
    if (!confirmed) return;

    try {
      await transferOwnership({
        variables: { clientId: client._id, newOwnerId: member.user._id },
      });
      feedback.snackbar({ text: 'Ownership transferred successfully', type: 'success' });
      refetch();
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const handleLeaveTeam = async () => {
    const confirmed = await feedback.confirm({
      title: 'Leave Team',
      text: 'Are you sure you want to leave this team? You will lose access to all company resources.',
      confirmButtonText: 'Leave',
    });
    if (!confirmed) return;

    try {
      await leaveClient({
        variables: { clientId: client._id },
      });
      feedback.snackbar({ text: 'You have left the team', type: 'success' });
      // User will be redirected due to losing client access
      window.location.reload();
    } catch (err) {
      feedback.snackbar({ text: parseGraphQLError(err), type: 'error' });
    }
  };

  const getRoleOptions = (memberRole) => {
    // Determine which roles the current user can assign
    const options = [];
    if (permissions.isOwner) {
      // Owner can set any role except owner (use transfer for that)
      options.push('admin', 'user', 'viewer');
    } else if (permissions.isAdmin) {
      options.push('admin', 'user', 'viewer');
    }
    // Always include current role in options
    if (!options.includes(memberRole)) {
      options.unshift(memberRole);
    }
    return options;
  };

  if (!client) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography level="h4">No company found</Typography>
        <Typography>You need to be part of a company to manage team members.</Typography>
      </Box>
    );
  }

  if (loading) return <LargeLoader />;

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography level="h4" color="danger">Error loading team</Typography>
        <Typography color="danger">{error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: '800px',
        mx: 'auto',
        borderRadius: 'sm',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography level="h1" sx={{ fontWeight: 'bold' }}>Team</Typography>
          <Typography level="body-md" color="neutral">
            Manage your team members and their permissions
          </Typography>
        </Box>
        {permissions.canManageTeam && (
          <Button
            startDecorator={<PersonAddIcon />}
            onClick={() => setInviteModalOpen(true)}
            color="primary"
          >
            Invite Member
          </Button>
        )}
      </Box>

      {/* Active Members */}
      <Card>
        <Box sx={{ mb: 1, p: 2, pb: 0 }}>
          <Typography level="title-md">Team Members</Typography>
          <Typography level="body-sm">
            {members.length} active member{members.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <Stack divider={<Divider />}>
            {members.map((member) => {
              const isCurrentUser = member.user._id === user._id;
              const canModify = permissions.canModifyMember(member.role) && !isCurrentUser;
              const canTransfer = permissions.canTransferOwnership && member.role !== 'owner' && !isCurrentUser;
              const roleOptions = getRoleOptions(member.role);

              return (
                <Box
                  key={member.user._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Avatar
                      size="md"
                      src={member.user.avatarUrl ? getCdnFileUrl(member.user.avatarUrl) : undefined}
                    >
                      {(member.user.firstName?.[0] || member.user.email[0]).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="title-sm" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {member.user.firstName} {member.user.lastName}
                        {isCurrentUser && (
                          <Chip size="sm" variant="soft" color="neutral">You</Chip>
                        )}
                      </Typography>
                      <Typography level="body-xs" color="neutral" sx={{ wordBreak: 'break-all' }}>
                        {member.user.email}
                      </Typography>
                      {member.joinedAt && (
                        <Typography level="body-xs" color="neutral">
                          Joined {formatTimestamp(member.joinedAt, 'MMM D, YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Role selector or badge */}
                    {canModify && roleOptions.length > 1 ? (
                      <Select
                        size="sm"
                        value={member.role}
                        onChange={(_, value) => handleRoleChange(member, value)}
                        disabled={updating}
                        sx={{ minWidth: 100 }}
                      >
                        {roleOptions.map((role) => (
                          <Option key={role} value={role}>
                            {TEAM_ROLE_LABELS[role]}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        size="sm"
                        variant="soft"
                        color={TEAM_ROLE_COLORS[member.role]}
                      >
                        {TEAM_ROLE_LABELS[member.role]}
                      </Chip>
                    )}

                    {/* Actions */}
                    {canTransfer && (
                      <Tooltip title="Transfer ownership">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => handleTransferOwnership(member)}
                          disabled={transferring}
                        >
                          <SwapHorizIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canModify && (
                      <Tooltip title="Remove member">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveMember(member)}
                          disabled={removing}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {permissions.canManageTeam && pendingInvites.length > 0 && (
        <Card>
          <Box sx={{ mb: 1, p: 2, pb: 0 }}>
            <Typography level="title-md">Pending Invitations</Typography>
            <Typography level="body-sm">
              {pendingInvites.length} pending invitation{pendingInvites.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <Stack divider={<Divider />}>
              {pendingInvites.map((invite) => (
                <Box
                  key={invite.user._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Avatar size="md" color="neutral">
                      <EmailIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography level="title-sm" sx={{ wordBreak: 'break-all' }}>
                        {invite.user.email}
                      </Typography>
                      {invite.invitedAt && (
                        <Typography level="body-xs" color="neutral">
                          Invited {formatTimestamp(invite.invitedAt, 'MMM D, YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={TEAM_ROLE_COLORS[invite.role]}
                    >
                      {TEAM_ROLE_LABELS[invite.role]}
                    </Chip>
                    <Tooltip title="Resend invitation">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleResendInvitation(invite)}
                        disabled={resending}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel invitation">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleCancelInvitation(invite)}
                        disabled={removing}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Leave Team */}
      {!permissions.isOwner && (
        <Card variant="outlined" color="danger">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography level="title-md">Leave Team</Typography>
                <Typography level="body-sm" color="neutral">
                  You will lose access to all company resources
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="danger"
                startDecorator={<LogoutIcon />}
                onClick={handleLeaveTeam}
                loading={leaving}
              >
                Leave Team
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      <TeamInviteModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        clientId={client._id}
        onSuccess={() => {
          setInviteModalOpen(false);
          refetch();
        }}
        isOwner={permissions.isOwner}
      />
    </Box>
  );
};

export default Team;
